import { DsAlert, DsBadge } from "../../../design-system";
import { ADMIN_RBAC_CATALOG, groupScreensForAccess } from "../../../rbac/catalog";
import { ACTION_SHORT_LABELS, LEVELS_WITH_ITEMS, dataLevelLabel, moduleStats, orderedActions } from "./roleAccessUtils";

function Card({ title, onEdit, children }) {
  return (
    <section className="rounded-xl border border-[hsl(var(--ds-border))] p-4">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{title}</h4>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
          >
            Change
          </button>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span className="text-[hsl(var(--ds-muted-fg))]">{label}</span>
      <span className="min-w-0 text-right font-medium text-[hsl(var(--ds-fg))]">{children}</span>
    </div>
  );
}

export default function ReviewStep({
  draft,
  isSystem,
  usesAccess,
  catalog,
  access,
  selectedModules,
  lockedModules,
  dataAccess,
  branches,
  departments,
  filterCount,
  activeEmployeeCount,
  isAdmin,
  delegableModules,
  warnings,
  onEditStep,
}) {
  const modules = catalog.filter((m) => selectedModules.has(m.key));
  const items = LEVELS_WITH_ITEMS.has(dataAccess?.level)
    ? ((dataAccess.level === "department" ? departments : branches) || []).filter((it) =>
        (dataAccess.selectedItems || []).map(Number).includes(Number(it.id))
      )
    : [];
  const assignment =
    draft.assignmentType === "all"
      ? `All employees${Number.isFinite(activeEmployeeCount) ? ` (${activeEmployeeCount})` : ""}`
      : draft.assignmentType === "filter"
        ? `${filterCount} selected employee${filterCount === 1 ? "" : "s"}`
        : "Not set";

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4">
      {warnings.map((w) => (
        <DsAlert key={w} tone="warning">
          {w}
        </DsAlert>
      ))}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card title="Role info" onEdit={isSystem ? null : () => onEditStep("info")}>
          <Fact label="Name">{draft.name || "—"}</Fact>
          <Fact label="Type">
            {isSystem ? "System · Access Based" : draft.roleType === "access_based" ? "Access Based" : "Action Based"}
          </Fact>
          {!isSystem ? (
            <>
              <Fact label="Status">
                <DsBadge tone={draft.active ? "success" : "warning"}>{draft.active ? "Active" : "Inactive"}</DsBadge>
              </Fact>
              <Fact label="Assigned to">{assignment}</Fact>
            </>
          ) : null}
          {draft.description ? <p className="mt-1.5 text-xs text-[hsl(var(--ds-muted-fg))]">{draft.description}</p> : null}
        </Card>

        {usesAccess ? (
          <Card title="Data access" onEdit={() => onEditStep("data")}>
            <Fact label="Can see">{dataLevelLabel(dataAccess?.level)}</Fact>
            {items.length ? <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">{items.map((it) => it.name).join(", ")}</p> : null}
          </Card>
        ) : (
          <Card title="Access">
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              Action Based: no screens or data access. Use this role as an assignee in workflows.
            </p>
          </Card>
        )}
      </div>

      {usesAccess ? (
        <Card title={`Modules & permissions (${modules.length})`} onEdit={() => onEditStep("permissions")}>
          <ul className="divide-y divide-[hsl(var(--ds-border))]">
            {modules.map((mod) => {
              const stats = moduleStats(mod, access?.[mod.key]);
              return (
                <li key={mod.key} className="py-2">
                  <details className="group">
                    <summary className="flex cursor-pointer list-none items-center gap-2 text-sm marker:hidden">
                      <span className="font-semibold text-[hsl(var(--ds-fg))]">{mod.label}</span>
                      {lockedModules.has(mod.key) ? <DsBadge tone="neutral">Unchanged</DsBadge> : null}
                      <span className="ml-auto text-xs text-[hsl(var(--ds-muted-fg))]">
                        {stats.screensOn}/{stats.screenTotal} screens · {stats.granted} permissions
                      </span>
                      <span className="text-xs text-[hsl(var(--ds-primary))] group-open:hidden">Show</span>
                      <span className="hidden text-xs text-[hsl(var(--ds-primary))] group-open:inline">Hide</span>
                    </summary>
                    <ul className="mt-2 space-y-1 pl-3">
                      {groupScreensForAccess(mod.screens).flatMap((g) =>
                        g.screens
                          .filter((s) => orderedActions(s).some((a) => access?.[mod.key]?.screens?.[s.key]?.[a]))
                          .map((s) => (
                            <li key={s.key} className="text-xs text-[hsl(var(--ds-fg))]">
                              <span className="text-[hsl(var(--ds-muted-fg))]">
                                {mod.label} › {g.label ? `${g.label} › ` : ""}
                              </span>
                              <span className="font-medium">{s.label}</span>
                              <span className="text-[hsl(var(--ds-muted-fg))]">
                                {" "}
                                —{" "}
                                {orderedActions(s)
                                  .filter((a) => access?.[mod.key]?.screens?.[s.key]?.[a])
                                  .map((a) => ACTION_SHORT_LABELS[a])
                                  .join(", ")}
                              </span>
                            </li>
                          ))
                      )}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
          {isAdmin && selectedModules.has("user_roles") ? (
            <p className="mt-3 border-t border-[hsl(var(--ds-border))] pt-3 text-xs text-[hsl(var(--ds-fg))]">
              <span className="font-semibold">Can grant to other roles:</span>{" "}
              {delegableModules.length
                ? delegableModules.map((k) => ADMIN_RBAC_CATALOG.find((m) => m.key === k)?.label || k).join(", ")
                : "No modules"}
            </p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
